import { useParams, Navigate } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";

const UPDATED = "June 2026";
const CONTENT = {
  terms: {
    title: "Terms of Service",
    intro: "These terms govern your use of DecodeKid. By creating an account you confirm you are 18 or over and a parent or guardian.",
    sections: [
      ["What DecodeKid is", "DecodeKid is a guidance tool that helps you understand your child's language, culture and moods. It is not therapy, medical advice, a diagnostic tool, or an emergency service, and it never accesses your child's devices or accounts."],
      ["Your account", "You are responsible for keeping your login secure. One account is for one parent or guardian. There are no child accounts anywhere on the service."],
      ["Free trial & subscriptions", "Every verified account receives a free 3-day trial of everything, with no card required. After the trial you may subscribe (weekly, monthly or yearly). Prices and renewal intervals are shown clearly before you pay."],
      ["Payments & no-refund policy", "Payments are handled by our payment provider; we never see or store your card details. Payments are final. You may cancel any time — access continues to the end of the period already paid for, and the subscription does not renew. Where your country's law grants a right to a refund, it is honoured."],
      ["Acceptable use", "Use DecodeKid only for lawful, personal parenting purposes. Do not attempt to access another user's data, disrupt the service, or use it to harm anyone."],
      ["Your content", "You own the text you enter and grant us a limited licence to process it solely to provide the service. DecodeKid owns its own content and brand."],
      ["Changes", "We may update these terms; material changes are notified by email and, where the law requires, need re-acceptance."],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro: "Your family's privacy is the heart of DecodeKid. We collect the minimum needed and protect child-related data with strong encryption.",
    sections: [
      ["What we collect", "Your name, email and account settings; child profiles that hold only an age band, an optional nickname or initial, and a style tag; the text you enter into the tools and the AI results; and basic billing status. We do not collect real names, photos, dates of birth, schools or any device data about your child."],
      ["How child data is protected", "Every child-data field and result is encrypted at rest using authenticated encryption (AES-256-GCM) with a key unique to your account. No DecodeKid staff member, support agent or admin can ever read your child profiles, saved outputs, or AI inputs and results."],
      ["How we use AI", "To create an answer, your text is sent securely to our AI provider only for that request, under an agreement that forbids training on it, forbids human review, and sets minimal retention. Phone numbers and emails in your text are masked automatically."],
      ["How we use data", "Only to provide the tools, handle billing and security, and improve the service. No advertising, no selling or sharing of data for advertising, and no marketing profiling."],
      ["Your rights", "You can access and export your data, correct your details, withdraw marketing consent, and delete your account at any time from the Account page. Contact us for any other privacy request."],
      ["Retention", "If your access ends, child data is kept encrypted for 90 days and then deleted. Server logs contain no child content and are kept briefly."],
      ["Processors", "We use trusted providers for hosting, database, AI, email and payments, each under a data-processing agreement."],
    ],
  },
  cookies: {
    title: "Cookie Policy",
    intro: "We keep cookies to a minimum and never use them to track anything about your child.",
    sections: [
      ["Essential cookies", "Required to keep you signed in and to protect the service. These are always on and cannot be turned off."],
      ["Analytics cookies", "Optional. They help us understand, anonymously, which tools help parents most. They never contain child data or the text you enter. You can accept or reject these in the cookie banner or at any time."],
      ["Managing cookies", "Use the cookie banner's Manage option to change your choice. You can also clear cookies in your browser settings."],
    ],
  },
  disclaimer: {
    title: "AI & Safety Disclaimer",
    intro: "DecodeKid is a gentle guide, not a substitute for professional help.",
    sections: [
      ["Not therapy or medical advice", "Nothing on DecodeKid is medical, psychological or therapeutic advice, and it never provides a diagnosis. If you have health concerns about your child, speak to a qualified professional."],
      ["AI-generated content", "Answers are generated by AI and framed as guidance. They can be imperfect. Use your judgement and knowledge of your own child."],
      ["In an emergency", "If you or your child may be in danger, contact your local emergency number immediately. Our Safety page lists helplines for several countries. DecodeKid is not an emergency service."],
    ],
  },
};

export default function Legal({ kind: kindProp }) {
  const params = useParams();
  const kind = kindProp || params.kind;
  const data = CONTENT[kind];
  if (!data) return <Navigate to="/404" replace />;

  return (
    <div>
      <PublicNav />
      <section className="dk-container py-16 max-w-3xl">
        <span className="badge-soft mb-3">Legal · Updated {UPDATED}</span>
        <h1 className="text-[32px] sm:text-[40px] font-semibold">{data.title}</h1>
        <p className="mt-3 text-[18px]" style={{ color: "var(--text-muted)" }}>{data.intro}</p>
        <div className="mt-8 space-y-6">
          {data.sections.map(([h, b], i) => (
            <div key={i} className="dk-card p-6">
              <h2 className="font-serif text-[20px] font-medium mb-2">{h}</h2>
              <p className="text-[15px]" style={{ color: "var(--text-muted)" }}>{b}</p>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
